import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import Stack, { contentStackSpacing } from '../Stack';
import { useIsDisabledByParent } from '../IsDisabledProvider';
import PressableDynamicOpacity from '../PressableDynamicOpacity';
import Text from '../Text';
import { colors } from '../../../utils/theme';
import { getSpacing } from '../../../utils/spacing';
import { View } from 'react-native';

export type PillOption = {
    label: string;
    value: string;
    testID?: string;
};

export type BasePillSelectOwnProps = {
    options: PillOption[];
    style?: StyleProp<ViewStyle>;
    pillContainerStyle?: StyleProp<ViewStyle>;
    isDisabled?: boolean;
    error?: string;
    testID?: string;
    disableEmpty?: boolean;
};

type SingleValueProps = {
    value: string | null;
    onChange: (value: string | null) => void;
};

export type PillSelectProps = BasePillSelectOwnProps & SingleValueProps;

export type MultiSelectPillSelectProps = BasePillSelectOwnProps & {
    value: string[] | null;
    onChange: (value: string[]) => void;
};

type BasePillSelectProps = BasePillSelectOwnProps & MultiSelectPillSelectProps;

const colorMap = {
    selected: {
        background: 'Grey',
        text: 'White',
    },
    unselected: {
        background: 'White',
        text: 'Dark',
    },
} as const;

const BasePillSelect: React.FC<BasePillSelectProps> = (props) => {
    const isDisabledByParent = useIsDisabledByParent(props.isDisabled);
    return (
        <Stack vertical style={props.style} space={1} align="flex-start">
            <View
                testID={props.testID}
                style={[
                    props.pillContainerStyle,
                    {
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        flexWrap: 'wrap',
                        gap: getSpacing(contentStackSpacing),
                    },
                ]}
            >
                {props.options.map((option) => {
                    const isSelected = !!props.value?.includes(option.value);
                    const color = isSelected ? colorMap.selected : colorMap.unselected;

                    return (
                        <PressableDynamicOpacity
                            isDisabled={isDisabledByParent}
                            key={option.value}
                            onPress={() => {
                                if (props.disableEmpty && isSelected) return;

                                props.onChange(
                                    isSelected
                                        ? (props.value || []).filter((value) => value !== option.value)
                                        : [...(props.value || []), option.value],
                                );
                            }}
                            testID={option.testID || `chip-${option.value}`}
                        >
                            <View
                                style={[
                                    props.style,
                                    {
                                        backgroundColor: colors[color.background],
                                        borderRadius: 5,
                                        borderColor: colors[isSelected ? color.background : color.text],
                                        borderWidth: 1,
                                        padding: 7,
                                    },
                                ]}
                            >
                                <Text variant="h5" color={color.text}>
                                    {option.label}
                                </Text>
                            </View>
                        </PressableDynamicOpacity>
                    );
                })}
            </View>
            {props.error ? <Text variant="input-error">{props.error}</Text> : null}
        </Stack>
    );
};

const PillSelect: React.FC<PillSelectProps> = ({ value, onChange, ...props }) => {
    const valueAsArray = value ? [value] : null;
    const onChangeAsArray = (newValue: string[] | null) => {
        const addedValue = newValue?.find((v) => !valueAsArray?.includes(v));
        onChange(addedValue ? addedValue : null);
    };
    return <BasePillSelect {...props} value={valueAsArray} onChange={onChangeAsArray} />;
};

export default PillSelect;

const MultiSelectPillSelect: React.FC<MultiSelectPillSelectProps> = (props) => {
    return <BasePillSelect {...props} />;
};

export { MultiSelectPillSelect };
